import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { HseFieldInfoLabel } from "../HseFieldInfoLabel";
import { fetchHseTiltak } from "../../../../api/hse/tiltak";
import { CreateRiskTiltakDialog } from "./CreateRiskTiltakDialog";
import {
  emptyRiskForm,
  isExcluded,
  needsTiltak,
  RISK_CATEGORIES,
  RISK_LEVELS,
} from "./riskUtils";

const inputClass =
  "mt-2 w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-900 outline-none focus:border-[#1463C7]";

function Accordion({ title, open, onToggle, children }) {
  return (
    <div className="overflow-hidden rounded-[14px] border border-slate-200">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-slate-800"
      >
        {title}
        <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? <div className="space-y-4 border-t border-slate-100 px-4 py-4">{children}</div> : null}
    </div>
  );
}

export function AssessRiskDialog({
  open,
  onClose,
  initialValues,
  saving,
  onSave,
  onExclude,
  onLinkTiltak,
  onEnsureSaved,
  linkedTiltak = [],
  categories = [],
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyRiskForm());
  const [extendedOpen, setExtendedOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [createTiltakOpen, setCreateTiltakOpen] = useState(false);
  const [existing, setExisting] = useState([]);
  const [linkId, setLinkId] = useState("");
  const [fileNames, setFileNames] = useState([]);
  const [savedId, setSavedId] = useState(initialValues?.id || "");

  useEffect(() => {
    if (!open) return;
    setForm(emptyRiskForm(initialValues || {}));
    setExtendedOpen(Boolean(initialValues?.work_task || initialValues?.likelihood));
    setFilesOpen(Boolean(initialValues?.files?.length));
    setCreateTiltakOpen(false);
    setSavedId(initialValues?.id || "");
    setFileNames((initialValues?.files || []).map((file) => file.name || file.filename || file));
  }, [open, initialValues, linkedTiltak.length]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetchHseTiltak({ per_page: 50 })
      .then((response) => {
        if (!cancelled) setExisting(response?.data || []);
      })
      .catch(() => {
        if (!cancelled) setExisting([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const showTiltakPrompt = needsTiltak(form.risk_level);
  const excluded = isExcluded(form);

  const availableToLink = useMemo(() => {
    const linkedIds = new Set((linkedTiltak || []).map((item) => String(item.id)));
    return existing.filter((item) => !linkedIds.has(String(item.id)));
  }, [existing, linkedTiltak]);

  const riskId = savedId || initialValues?.id || form.id;

  const ensureRisk = async () => {
    if (riskId) return { ...form, id: riskId };
    if (!onEnsureSaved) {
      toast.error(t("hse.risk.tiltak.save_first"));
      return null;
    }
    const saved = await onEnsureSaved({ ...form, files: fileNames, status: "draft" });
    if (saved?.id) {
      setSavedId(saved.id);
      setForm((current) => ({ ...current, id: saved.id }));
    }
    return saved;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error(t("hse.risk.error_title"));
      return;
    }
    if (!form.risk_level && !excluded) {
      toast.error(t("hse.risk.error_level"));
      return;
    }
    await onSave({ ...form, files: fileNames });
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("hse.risk.assess.title")}
      size="3xl"
      closeOnOutsideClick
    >
      <form onSubmit={handleSave} className="space-y-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("hse.risk.assess.required_hint")}
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <HseFieldInfoLabel
              label={`${t("hse.risk.fields.title")} *`}
              infoTitle="hse.risk.info.title.title"
              infoBody="hse.risk.info.title.body"
            />
            <input
              className={inputClass}
              value={form.title}
              onChange={(event) => setField("title", event.target.value)}
            />
          </label>
          <label className="block">
            <HseFieldInfoLabel
              label={t("hse.risk.fields.category")}
              infoTitle="hse.risk.info.category.title"
              infoBody="hse.risk.info.category.body"
            />
            <select
              className={inputClass}
              value={form.hse_risk_category_id || ""}
              onChange={(event) => {
                const selected = categories.find(
                  (category) => String(category.id) === event.target.value
                );
                setForm((current) => ({
                  ...current,
                  hse_risk_category_id: selected?.id || "",
                  category: selected?.slug || "",
                  category_name: selected?.name || "",
                }));
              }}
            >
              <option value="">{t("hse.risk.fields.choose")}</option>
              {(categories.length
                ? categories
                : RISK_CATEGORIES.map((slug) => ({ id: slug, slug, name: t(`hse.risk.category.${slug}`) }))
              ).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name || t(`hse.risk.category.${category.slug}`, { defaultValue: category.slug })}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <HseFieldInfoLabel
            label={t("hse.risk.fields.description")}
            infoTitle="hse.risk.info.description.title"
            infoBody="hse.risk.info.description.body"
          />
          <textarea
            rows={3}
            className={inputClass}
            placeholder={t("hse.risk.fields.description_placeholder")}
            value={form.description}
            onChange={(event) => setField("description", event.target.value)}
          />
        </label>

        <label className="block">
          <HseFieldInfoLabel
            label={t("hse.risk.fields.consequence")}
            infoTitle="hse.risk.info.consequence.title"
            infoBody="hse.risk.info.consequence.body"
          />
          <textarea
            rows={3}
            className={inputClass}
            placeholder={t("hse.risk.fields.consequence_placeholder")}
            value={form.consequence}
            onChange={(event) => setField("consequence", event.target.value)}
          />
        </label>

        <label className="block">
          <HseFieldInfoLabel
            label={`${t("hse.risk.fields.level")} *`}
            infoTitle="hse.risk.info.level.title"
            infoBody="hse.risk.info.level.body"
          />
          <select
            className={inputClass}
            value={form.risk_level}
            onChange={(event) => setField("risk_level", event.target.value)}
          >
            <option value="">{t("hse.risk.fields.level_placeholder")}</option>
            {RISK_LEVELS.map((level) => (
              <option key={level} value={level}>
                {t(`hse.risk.level.${level}`)}
              </option>
            ))}
          </select>
          <p className="mt-2 text-sm text-[#1463C7]">{t("hse.risk.assess.how_to")}</p>
        </label>

        {showTiltakPrompt ? (
          <div className="rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {t("hse.risk.assess.tiltak_prompt")}
          </div>
        ) : null}

        <Accordion
          title={t("hse.risk.assess.extended")}
          open={extendedOpen}
          onToggle={() => setExtendedOpen((value) => !value)}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.fields.work_task")}</span>
              <textarea
                rows={2}
                className={inputClass}
                value={form.work_task}
                onChange={(event) => setField("work_task", event.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.fields.cause")}</span>
              <textarea
                rows={2}
                className={inputClass}
                value={form.triggering_cause}
                onChange={(event) => setField("triggering_cause", event.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.fields.existing")}</span>
              <textarea
                rows={2}
                className={inputClass}
                value={form.existing_measures}
                onChange={(event) => setField("existing_measures", event.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.fields.history")}</span>
              <textarea
                rows={2}
                className={inputClass}
                value={form.history}
                onChange={(event) => setField("history", event.target.value)}
              />
            </label>
            <label className="block">
              <HseFieldInfoLabel
                label={t("hse.risk.fields.likelihood")}
                infoTitle="hse.risk.info.matrix.title"
                infoBody="hse.risk.info.matrix.body"
              />
              <select
                className={inputClass}
                value={form.likelihood}
                onChange={(event) => setField("likelihood", event.target.value)}
              >
                <option value="">{t("hse.risk.fields.choose")}</option>
                {[1, 2, 3, 4, 5].map((value) => (
                  <option key={value} value={value}>
                    {value} — {t(`hse.risk.scale.likelihood.${value}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[13px] font-bold text-slate-700">
                {t("hse.risk.fields.consequence_score")}
              </span>
              <select
                className={inputClass}
                value={form.consequence_score}
                onChange={(event) => setField("consequence_score", event.target.value)}
              >
                <option value="">{t("hse.risk.fields.choose")}</option>
                {[1, 2, 3, 4, 5].map((value) => (
                  <option key={value} value={value}>
                    {value} — {t(`hse.risk.scale.consequence.${value}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </Accordion>

        <Accordion title={t("hse.risk.assess.files")} open={filesOpen} onToggle={() => setFilesOpen((value) => !value)}>
          <input
            type="file"
            multiple
            onChange={(event) =>
              setFileNames(Array.from(event.target.files || []).map((file) => file.name))
            }
            className="block w-full text-sm text-slate-600"
          />
          {fileNames.length > 0 ? (
            <ul className="text-sm text-slate-600">
              {fileNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">{t("hse.risk.assess.files_empty")}</p>
          )}
        </Accordion>

        <div className="space-y-3 rounded-[14px] border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-extrabold text-slate-900">{t("hse.risk.tiltak.how")}</h3>
            <button
              type="button"
              onClick={async () => {
                const saved = await ensureRisk();
                if (saved?.id) setCreateTiltakOpen(true);
              }}
              className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4" />
              {t("hse.risk.tiltak.new")}
            </button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              className={inputClass}
              value={linkId}
              onChange={(event) => setLinkId(event.target.value)}
            >
              <option value="">{t("hse.risk.tiltak.link_existing")}</option>
              {availableToLink.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!linkId}
              onClick={async () => {
                const saved = await ensureRisk();
                if (!saved?.id) return;
                await onLinkTiltak?.(linkId);
                setLinkId("");
              }}
              className="mt-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
            >
              {t("hse.risk.tiltak.link")}
            </button>
          </div>
          {linkedTiltak.length === 0 ? (
            <p className="text-sm text-slate-500">{t("hse.risk.tiltak.empty")}</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {linkedTiltak.map((item) => (
                <li key={item.id} className="flex items-center justify-between py-2">
                  <span className="font-medium text-slate-800">{item.title}</span>
                  <span className="text-xs text-slate-500">
                    {t(`hse.tiltak.status.${item.status || "planned"}`)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-slate-600 hover:text-slate-900"
          >
            {t("hse.risk.cancel")}
          </button>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onExclude?.(form)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
            >
              {t("hse.risk.exclude")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-[#1463C7] px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? t("hse.risk.saving") : t("hse.risk.save")}
            </button>
          </div>
        </div>
      </form>

      {riskId ? (
        <CreateRiskTiltakDialog
          open={createTiltakOpen}
          onClose={() => setCreateTiltakOpen(false)}
          risk={{ ...form, id: riskId }}
          onCreated={(created) => onLinkTiltak?.(created?.id, created)}
        />
      ) : null}
    </PMDialog>
  );
}
